'use client';

import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';

import { areArrayEquals } from '@/components/RefreshContent';
import { useCustomToast } from '@/hooks';
import { TOAST_TYPE } from '@/hooks/useCustomToast';
import { useQueryClient } from '@tanstack/react-query';

export const MIN_INTERVAL = 120000;

export function useRefreshContent(
  intervalRef: MutableRefObject<number>,
  dataFromResponse: any[],
  showToastOnChange = true,
) {
  const queryClient = useQueryClient();

  // Initialize with dataFromResponse to avoid empty state on mount
  const [currentData, setCurrentData] = useState<any[]>(dataFromResponse || []);
  const [canShowToast, setCanShowToast] = useState<boolean>(true);

  // Track the previous data reference to detect complete data changes
  const prevDataRef = useRef<any[]>(dataFromResponse);

  const toastId = useRef<any>(null);

  useEffect(() => {
    return () => {
      if (toastId.current !== null) {
        toast.dismiss(toastId.current);
      }
    };
  }, []);

  const { showToast } = useCustomToast();

  // Update currentData and reinit intervalRef
  const updateData = useCallback(() => {
    intervalRef.current = MIN_INTERVAL;

    queryClient.setQueryData(['homeFeed'], dataFromResponse);
    setCurrentData(dataFromResponse);
    setCanShowToast(true);
  }, [dataFromResponse, intervalRef, queryClient]);

  // Scroll to top function, called only when the user clicks on the toast
  const scrollToTop = useCallback(() => {
    document
      .querySelector('#content')
      ?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    // If currentData is empty and we receive data, initialize immediately
    if (currentData.length === 0 && dataFromResponse.length > 0) {
      setCurrentData(dataFromResponse);
      prevDataRef.current = dataFromResponse;
      return;
    }

    // If dataFromResponse is empty, don't update (wait for data)
    if (!dataFromResponse || dataFromResponse.length === 0) {
      return;
    }

    // Skip comparison if dataFromResponse is the same reference as currentData
    // This happens when React Query returns cached data
    if (currentData === dataFromResponse) {
      return;
    }

    // Detect if this is a completely different dataset (navigation scenario)
    // If the reference changed from what we tracked before, update immediately
    if (prevDataRef.current !== dataFromResponse) {
      prevDataRef.current = dataFromResponse;

      // Check if data structure is significantly different (navigation between pages)
      const isDifferentDataset =
        currentData.length > 0 &&
        dataFromResponse.length > 0 &&
        (currentData[0]?.id !== dataFromResponse[0]?.id ||
          currentData[0]?.category?.id !== dataFromResponse[0]?.category?.id);

      if (isDifferentDataset) {
        // Different page - update without toast
        setCurrentData(dataFromResponse);
        setCanShowToast(true);
        return;
      }
    }

    // Check if the currentData and the data from the response have changes
    const arraysAreEqual = areArrayEquals(currentData, dataFromResponse);
    const hasNewResults = !arraysAreEqual;

    if (hasNewResults) {
      // Show custom toast to indicate changes

      if (canShowToast && showToastOnChange) {
        toastId.current = showToast({
          message: 'Haz click para ver el nuevo contenido disponible',
          type: TOAST_TYPE.INFO,
          options: {
            autoClose: false,
            position: 'top-center',
            closeOnClick: true,
            onClick: () => {
              updateData();
              scrollToTop();
            },
          },
        });

        setCanShowToast(false);
      } else {
        toast.update(toastId.current, {
          onClick: () => {
            updateData();
            scrollToTop();
          },
        });
      }
    } else {
      // Data is the same, increase interval (exponential backoff)
      intervalRef.current = intervalRef.current * 2;
    }

    // eslint-disable-next-line
  }, [dataFromResponse]);

  useEffect(() => {
    // check if the next page was fetched
    if (dataFromResponse.length > currentData?.length) {
      updateData();
    }

    // eslint-disable-next-line
  }, [currentData, dataFromResponse]);

  return { currentData };
}
